import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Chip,
  Avatar,
  styled,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getAdminPayouts, createPayouts, updatePayoutStatus } from '../../../redux/actions/adminActions';
import { fCurrency } from '../../../utils/formatNumber';
import { fDateTime } from '../../../utils/formatTime';
import TransitionComponent from '../../Logged/TransitionComponent';

const TableCellStyled = styled(TableCell)(({ theme }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('md')]: {
    padding: '8px',
  },
}));

const getStatusColor = (status) => {
  switch (status) {
    case 'paid':
      return 'success';
    case 'processed':
      return 'success';
    case 'pending':
      return 'warning';
    case 'processing':
      return 'info';
    case 'failed':
      return 'error';
    case 'cancelled':
      return 'error';
    default:
      return 'default';
  }
};

// Status mapping (same as in script)
const statusMap = {
  Pagado: 'paid',
  'En Camino': 'processing',
  Programado: 'pending',
  Ajuste: 'adjustment',
};

// Month name to number mapping (same as in script)
const monthMap = {
  January: '01',
  February: '02',
  March: '03',
  April: '04',
  May: '05',
  June: '06',
  July: '07',
  August: '08',
  September: '09',
  October: '10',
  November: '11',
  December: '12',
};

function AdminPayoutsList() {
  const dispatch = useDispatch();
  const payouts = useSelector((state) => state.admin.payouts);
  const loadingPayouts = useSelector((state) => state.admin.loadingPayouts);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [rawPayoutsText, setRawPayoutsText] = useState('');
  const [parsedPayouts, setParsedPayouts] = useState([]);
  const [processingStep, setProcessingStep] = useState('input'); // 'input', 'review', 'creating', 'result'
  const [createResult, setCreateResult] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null); // Track which action is loading

  useEffect(() => {
    dispatch(getAdminPayouts());
  }, [dispatch]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const parsePayoutsData = (text) => {
    setParseError(null);

    if (!text.trim()) {
      throw new Error('Please enter payout data');
    }

    const lines = text.split('\n').filter((line) => line.trim());

    const parsed = lines
      .map((row, index) => {
        const columns = row.split('\t');

        if (columns.length < 11) {
          throw new Error(`Row ${index + 1}: Invalid format. Expected at least 11 columns, got ${columns.length}`);
        }

        // Convert date from "Month DD, YYYY" to MySQL DATETIME format
        const dateParts = columns[0].match(/(\w+) (\d+), (\d{4})/);
        if (!dateParts) {
          throw new Error(`Row ${index + 1}: Invalid date format in: ${columns[0]}`);
        }
        const month = monthMap[dateParts[1]];
        const day = dateParts[2].padStart(2, '0');
        const year = dateParts[3];
        const mysqlDate = `${year}-${month}-${day} 06:00:00`;

        // Clean monetary values
        const cleanAmount = (value) => value.replace('$', '').replace(/,/g, '');

        const storeName = columns[6];

        // Check if the last column contains a valid URL
        const lastColumn = columns[11] && columns[11].trim() && columns[11].trim() !== '\r' ? columns[11].trim() : '';
        const isValidUrl = lastColumn && (lastColumn.startsWith('http://') || lastColumn.startsWith('https://'));

        // Create the entry - send store_name instead of id_store, let backend handle validation
        // Updated column indexes: skip columns 3, 7, and 10
        const entry = {
          date: mysqlDate,
          status: statusMap[columns[1]] || 'pending',
          total_amount: cleanAmount(columns[2]),
          reference: columns[4], // Updated from columns[3] (skip column 3)
          payout_fee: columns[5] === '-' ? 0 : cleanAmount(columns[5]), // Updated from columns[4]
          store_name: storeName, // Send store name to backend (columns[6])
          account_name: columns[8], // Updated from columns[6] (skip column 7)
          account_number: columns[9].replace('\r', ''), // Updated from columns[7]
          external_reference: isValidUrl ? lastColumn : '', // Only use if it's a valid URL
          originalRow: index + 1,
        };

        return entry;
      })
      .filter((entry) => entry.account_name !== 'Pendiente');

    return parsed;
  };

  const handleProcessPayouts = () => {
    try {
      const parsed = parsePayoutsData(rawPayoutsText);
      setParsedPayouts(parsed);
      setProcessingStep('review');
    } catch (error) {
      setParseError(error.message);
    }
  };

  const handleConfirmCreate = async () => {
    setProcessingStep('creating');
    try {
      const result = await dispatch(createPayouts(parsedPayouts));
      setCreateResult(result);
      setProcessingStep('result');
    } catch (error) {
      setParseError(error.message);
      setProcessingStep('review');
    }
  };

  const handleCloseModal = () => {
    setCreateModalOpen(false);
    setRawPayoutsText('');
    setParsedPayouts([]);
    setProcessingStep('input');
    setCreateResult(null);
    setParseError(null);
  };

  const handleOpenCreateModal = () => {
    setCreateModalOpen(true);
  };

  const handleMarkAsPaid = async (payoutId) => {
    setActionLoading(payoutId);
    try {
      await dispatch(updatePayoutStatus(payoutId, 'paid'));
    } catch (error) {
      console.error('Error marking payout as paid:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRetryNequi = async (payoutId) => {
    setActionLoading(payoutId);
    try {
      await dispatch(updatePayoutStatus(payoutId));
    } catch (error) {
      console.error('Error retrying Nequi payout:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const visiblePayouts = React.useMemo(() => {
    return payouts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [page, rowsPerPage, payouts]);

  const renderActions = (payout) => {
    const canTakeAction = ['pending', 'processing'].includes(payout.status);

    if (!canTakeAction) {
      return (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      );
    }

    const isLoading = actionLoading === payout.id_payout;
    const isNequi = payout.account_name === 'Nequi';

    if (isNequi) {
      return (
        <Tooltip title="Retry Nequi dispersion">
          <IconButton size="small" onClick={() => handleRetryNequi(payout.id_payout)} disabled={isLoading} color="primary">
            {isLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      );
    }

    return (
      <Tooltip title="Mark as paid">
        <IconButton size="small" onClick={() => handleMarkAsPaid(payout.id_payout)} disabled={isLoading} color="success">
          {isLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
        </IconButton>
      </Tooltip>
    );
  };

  const renderModalContent = () => {
    switch (processingStep) {
      case 'input':
        return (
          <>
            <DialogTitle>Create New Payouts</DialogTitle>
            <DialogContent>
              <TextField
                fullWidth
                multiline
                rows={12}
                variant="outlined"
                label="Paste tab-separated payout data"
                value={rawPayoutsText}
                onChange={(e) => setRawPayoutsText(e.target.value)}
                placeholder="June 3, 2025    Pagado    $980,609    1    MPL-2025-2810    $2,500    latamplay.com    MARIA ALEJANDRA NOVA    Bancolombia    5200002436    Semanal    https://docs.google.com/spreadsheets/..."
                sx={{ mt: 2 }}
              />
              {parseError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {parseError}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseModal}>Cancel</Button>
              <Button onClick={handleProcessPayouts} variant="contained" disabled={!rawPayoutsText.trim()}>
                Process Data
              </Button>
            </DialogActions>
          </>
        );

      case 'review':
        return (
          <>
            <DialogTitle>{`Review Payouts (${parsedPayouts.length} items)`}</DialogTitle>
            <DialogContent>
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Summary
                  </Typography>
                  <Typography>{`Total Amount: ${fCurrency(parsedPayouts.reduce((sum, p) => sum + parseFloat(p.total_amount), 0))}`}</Typography>
                  <Typography>{`Total Fees: ${fCurrency(parsedPayouts.reduce((sum, p) => sum + parseFloat(p.payout_fee), 0))}`}</Typography>
                </CardContent>
              </Card>

              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                <List dense>
                  {parsedPayouts.map((payout) => (
                    <React.Fragment key={payout.reference}>
                      <ListItem>
                        <ListItemText
                          primary={`${payout.reference} - ${payout.store_name}`}
                          secondary={`${fCurrency(payout.total_amount)} | ${payout.account_name}: ${payout.account_number} | Status: ${payout.status}`}
                        />
                      </ListItem>
                      {parsedPayouts.indexOf(payout) < parsedPayouts.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Box>

              {parseError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {parseError}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setProcessingStep('input')}>Back</Button>
              <Button onClick={handleConfirmCreate} variant="contained" color="primary">
                Create Payouts
              </Button>
            </DialogActions>
          </>
        );

      case 'creating':
        return (
          <>
            <DialogTitle>Creating Payouts...</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
              <Typography align="center">{`Processing ${parsedPayouts.length} payouts...`}</Typography>
            </DialogContent>
          </>
        );

      case 'result':
        return (
          <>
            <DialogTitle>Creation Results</DialogTitle>
            <DialogContent>
              {createResult && (
                <Box>
                  <Alert severity={createResult.result.results.every((r) => r.success) ? 'success' : 'warning'} sx={{ mb: 2 }}>
                    {createResult.result.message}
                  </Alert>

                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    <List dense>
                      {createResult.result.results.map((result) => (
                        <React.Fragment key={result.reference}>
                          <ListItem>
                            <ListItemText
                              primary={(
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip label={result.success ? 'Success' : 'Failed'} color={result.success ? 'success' : 'error'} size="small" />
                                  {result.reference}
                                </Box>
                              )}
                              secondary={
                                result.success
                                  ? `ID: ${result.id}${result.finalStatus ? ` | Final Status: ${result.finalStatus}` : ''}`
                                  : `Error: ${result.error}`
                              }
                            />
                          </ListItem>
                          {createResult.result.results.indexOf(result) < createResult.result.results.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseModal} variant="contained">
                Close
              </Button>
            </DialogActions>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <TransitionComponent>
      <Box sx={{ width: '100%', overflow: 'auto', p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Payouts
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateModal} sx={{ minWidth: 'auto' }}>
            Add Payouts
          </Button>
        </Box>

        <Paper sx={{ width: '100%', mb: 2, border: '1px solid #919EAB3D' }}>
          <TableContainer>
            <Table sx={{ minWidth: { xs: '100%', sm: 750 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Store</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Fee</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingPayouts ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : visiblePayouts.length > 0 ? (
                  visiblePayouts.map((payout) => (
                    <TableRow hover tabIndex={-1} key={payout.id_payout}>
                      <TableCellStyled>{payout.id_payout}</TableCellStyled>
                      <TableCellStyled>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar src={payout.logo_image} alt={payout.store_name} sx={{ width: 24, height: 24 }}>
                            <AccountBalanceIcon fontSize="small" />
                          </Avatar>
                          <Typography variant="body2" noWrap>
                            {payout.store_name || 'Unknown Store'}
                          </Typography>
                        </Box>
                      </TableCellStyled>
                      <TableCellStyled>{payout.reference || '-'}</TableCellStyled>
                      <TableCellStyled>{fCurrency(payout.total_amount)}</TableCellStyled>
                      <TableCellStyled>{fCurrency(payout.payout_fee)}</TableCellStyled>
                      <TableCellStyled>
                        <Chip label={payout.status} color={getStatusColor(payout.status)} size="small" />
                      </TableCellStyled>
                      <TableCellStyled>{payout.paid_date ? fDateTime(payout.paid_date) : '-'}</TableCellStyled>
                      <TableCellStyled>{renderActions(payout)}</TableCellStyled>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No payouts found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={payouts.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>

        {/* Create Payouts Modal */}
        <Dialog
          open={createModalOpen}
          onClose={handleCloseModal}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { minHeight: '500px' },
          }}
        >
          {renderModalContent()}
        </Dialog>
      </Box>
    </TransitionComponent>
  );
}

export default AdminPayoutsList;
